import isEmpty from 'lodash/isEmpty';
import querystring from 'query-string';
import { defineMessages } from 'react-intl';
import { getAdminEntities, isAdminEntity, isMemberEntity } from './jwt';
import { isEfentoDevice, isLupusDevice } from './deviceConfig';

export const DISTRIBUTOR = 'DISTRIBUTOR';
export const ACCOUNT = 'ACCOUNT';
export const UNDEFINED = 'UNDEFINED';
export const ASSET = 'ASSET';

export const messages = defineMessages({
  DISTRIBUTOR: {
    id: 'Entity.DISTRIBUTOR',
    defaultMessage: 'Distributor',
  },
  ACCOUNT: {
    id: 'Entity.ACCOUNT',
    defaultMessage: 'Account',
  },
  UNDEFINED: {
    id: 'Entity.UNDEFINED',
    defaultMessage: 'Undefined',
  },
  ASSET: {
    id: 'Entity.ASSET',
    defaultMessage: 'Asset',
  },
  DEVICE: {
    id: 'LABEL.DEVICE',
    defaultMessage: 'Device',
  },
  ENTITY_TYPE: {
    id: 'Entity.ENTITY_TYPE',
    defaultMessage: 'Entity Type',
  },
  IS_EXPANDED: {
    id: 'Entity.IS_EXPANDED',
    defaultMessage: 'Is Expanded',
  },
  IS_RESTRICTED: {
    id: 'Entity.IS_RESTRICTED',
    defaultMessage: 'Restricted',
  },
});

function tree(entities, devices, admin_entities, filter_root_entity_id = null, device_state = {}) {
  const data = Object.values(entities);
  return data
    .filter((node) => (filter_root_entity_id ? node.id === filter_root_entity_id : !node.parent_id))
    .map((child) => {
      const can_admin = isEntityAdmin(entities, child.id, admin_entities);
      // eslint-disable-next-line no-param-reassign
      device_state = {};
      // eslint-disable-next-line no-param-reassign
      child.children = [
        ...traverse(entities, data, devices, child.id, admin_entities, can_admin, device_state),
        ...getDevices(
          devices,
          child.id,
          isEntityAdminForEntityType(entities, child.id, DISTRIBUTOR, admin_entities) ||
            isEntityAdminForEntityType(entities, child.id, ACCOUNT, admin_entities),
          device_state
        ),
      ];

      if (device_state.has_invalid_measurements) {
        // eslint-disable-next-line no-param-reassign
        child.has_iv_devices = true;
      }

      if (device_state.has_low_battery) {
        // eslint-disable-next-line no-param-reassign
        child.has_low_battery_devices = true;
      }

      // eslint-disable-next-line no-param-reassign
      child.key = child.id;
      // eslint-disable-next-line no-param-reassign
      child.draggable = false; // restrict all root entities
      // eslint-disable-next-line no-param-reassign
      child.can_admin = can_admin;
      // eslint-disable-next-line no-param-reassign
      child.style = { padding: 0, paddingBottom: '.1rem' };
      return child;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

function traverse(
  entities,
  data,
  devices,
  parentId,
  admin_entities,
  parent_can_admin,
  device_state = {}
) {
  const children = data.filter((each) => each.parent_id === parentId);
  children.forEach((child) => {
    const can_admin = isEntityAdmin(entities, child.id, admin_entities);
    // eslint-disable-next-line no-param-reassign
    device_state = {};
    // eslint-disable-next-line no-param-reassign
    child.children = [
      ...traverse(entities, data, devices, child.id, admin_entities, can_admin, device_state),
      ...getDevices(
        devices,
        child.id,
        isEntityAdminForEntityType(entities, child.id, DISTRIBUTOR, admin_entities) ||
          isEntityAdminForEntityType(entities, child.id, ACCOUNT, admin_entities),
        device_state
      ),
    ];

    if (device_state.has_invalid_measurements) {
      // eslint-disable-next-line no-param-reassign
      child.has_iv_devices = true;
    }

    if (device_state.has_low_battery) {
      // eslint-disable-next-line no-param-reassign
      child.has_low_battery_devices = true;
    }

    // eslint-disable-next-line no-param-reassign
    child.key = child.id;
    if (child.is_restricted) {
      // eslint-disable-next-line no-param-reassign
      child.draggable = false;
    } else {
      // eslint-disable-next-line no-param-reassign
      child.draggable = parent_can_admin;
    }
    // eslint-disable-next-line no-param-reassign
    child.can_admin = can_admin;
    // eslint-disable-next-line no-param-reassign
    child.style = { padding: 0, paddingBottom: '.1rem' };
    // child.style = { padding: 0, paddingBottom: '.25rem', paddingLeft: '.25rem' };
  });
  return children.sort((a, b) => a.name.localeCompare(b.name));
}

function getDevices(devices, entity_id, can_admin, device_state = {}) {
  if (devices && devices.length) {
    return devices
      .filter((d) => d.entity_id === entity_id)
      .map((d) => {
        const has_invalid_measurements = hasCurrentInvalidMeasurementState(d);
        if (!device_state.has_invalid_measurements && has_invalid_measurements) {
          // eslint-disable-next-line no-param-reassign
          device_state.has_invalid_measurements = true;
        }
        const has_low_battery = getCurrentBatteryState(d)[2];
        if (!device_state.has_low_battery && has_low_battery) {
          // eslint-disable-next-line no-param-reassign
          device_state.has_low_battery = true;
        }
        return {
          ...d,
          key: d.id,
          entity_type: 'DEVICE',
          can_admin: can_admin,
          has_error: configHasErrors(getCurrentConfigState(d)[1]),
          has_invalid_measurements: has_invalid_measurements,
          fw_version: getCurrentFWVersion(
            getCurrentDeviceInfoState(d)[1] || getCurrentLupusDeviceInfoState(d)[1]
          ),
          battery_state: getCurrentBatteryState(d),
          device_info: getCurrentDeviceInfoState(d)[1],
          draggable: can_admin,
          droppable: false,
          optional_sensor_id: d.config && d.config.optional_sensor_id,
          style: { padding: 0, paddingBottom: '.1rem' },
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  return [];
}

export function createEntityTree(entities, devices, filter_root_entity_id) {
  if (!isEmpty(entities)) {
    if (!devices) {
      // eslint-disable-next-line no-param-reassign
      devices = {};
    }
    return tree(entities, Object.values(devices), getAdminEntities(), filter_root_entity_id);
  }
  return [];
}

export function getRootEntityIDFromQuery(entities, search) {
  if (!isEmpty(entities)) {
    if (search && search.length > 0) {
      const queryParams = querystring.parse(search);
      if (queryParams && queryParams.r && queryParams.r.length > 0 && entities[queryParams.r]) {
        return queryParams.r;
      }
    }
  }
  return null;
}

export function getParentIDFromEntityID(entities, entity_id) {
  if (!isEmpty(entities)) {
    if (entities[entity_id] && entities[entity_id].parent_id) {
      return entities[entity_id].parent_id;
    }
  }
  return null;
}

export function getExpandedKeys(entities, filter_root_entity_id) {
  const expandedKeys = {};
  if (!isEmpty(entities)) {
    Object.values(entities).forEach((e) => {
      if (e && e.config && e.config.is_expanded) {
        expandedKeys[e.id] = true;
      }
    });
    // check if only one root element
    const rootElements = Object.values(entities).filter((r) => !r.parent_id);
    if (rootElements.length === 1) {
      expandedKeys[rootElements[0].id] = true;
    }
    if (filter_root_entity_id) {
      expandedKeys[filter_root_entity_id] = true;
    }
  }
  return expandedKeys;
}

function hasEntityTypeMatch(entities, entity_id, entity_type) {
  const entity = entities[entity_id];
  if (entity) {
    if (entity.entity_type === entity_type) {
      return true;
    }
    if (entity.parent_id && entities[entity.parent_id]) {
      return hasEntityTypeMatch(entities, entity.parent_id, entity_type);
    }
  }
  return false;
}

export function hasParentEntityType(entities, entity_id, parent_entity_type) {
  // loop all parent entities
  return hasEntityTypeMatch(entities, entity_id, parent_entity_type);
}

function hasMatchReturnMatch(entities, entity_id, entity_type) {
  const entity = entities[entity_id];
  if (entity) {
    if (entity.entity_type === entity_type) {
      return entity;
    }
    if (entity.parent_id && entities[entity.parent_id]) {
      return hasMatchReturnMatch(entities, entity.parent_id, entity_type);
    }
  }
  return null;
}

export function hasParentMMSAccountEntity(entities, entity_id) {
  const match = hasMatchReturnMatch(entities, entity_id, ACCOUNT);
  if (match && match.data && match.data.has_mms) {
    return true;
  }
  return false;
}

export function getParentAccountEntity(entities, entity_id) {
  return hasMatchReturnMatch(entities, entity_id, ACCOUNT);
}

export function getParentDistributorEntity(entities, entity_id) {
  return hasMatchReturnMatch(entities, entity_id, DISTRIBUTOR);
}

function hasAdminEntityMatch(entities, entity_id, admin_entities) {
  const entity = entities[entity_id];
  if (entity && admin_entities && admin_entities.length) {
    if (admin_entities.includes(entity.id)) {
      return true;
    }
    if (entity.parent_id && entities[entity.parent_id]) {
      return hasAdminEntityMatch(entities, entity.parent_id, admin_entities);
    }
  }
  return false;
}

export function isEntityAdmin(entities, entity_id, admin_entities) {
  if (!admin_entities || !admin_entities.length) {
    // eslint-disable-next-line no-param-reassign
    admin_entities = getAdminEntities();
  }
  if (admin_entities && admin_entities.length) {
    return hasAdminEntityMatch(entities, entity_id, admin_entities);
  }
  return false;
}

function hasEntityAdminForEntityType(entities, entity_id, admin_entities, entity_type) {
  const entity = entities[entity_id];
  if (entity && admin_entities && admin_entities.length) {
    if (entity.entity_type === entity_type && admin_entities.includes(entity.id)) {
      return true;
    }
    if (entity.parent_id && entities[entity.parent_id]) {
      return hasEntityAdminForEntityType(entities, entity.parent_id, admin_entities, entity_type);
    }
  }
  return false;
}

export function isEntityAdminForEntityType(entities, entity_id, entity_type, admin_entities) {
  if (!admin_entities || !admin_entities.length) {
    // eslint-disable-next-line no-param-reassign
    admin_entities = getAdminEntities();
  }
  if (admin_entities && admin_entities.length) {
    return hasEntityAdminForEntityType(entities, entity_id, admin_entities, entity_type);
  }
  return false;
}

export function hasEntityTypeNonRecursive(entities, entity_type) {
  let match = false;
  if (entities) {
    Object.values(entities).forEach((e) => {
      if (match) {
        return;
      }
      if (e.entity_type === entity_type) {
        match = true;
      }
    });
  }

  return match;
}

function hasMMSAdminEntityTypeMatch(entities, entity_id, admin_entities, entity_type) {
  const entity = entities[entity_id];
  if (entity && admin_entities && admin_entities.length) {
    if (
      entity.entity_type === entity_type &&
      entity.data.has_mms &&
      admin_entities.includes(entity.id)
    ) {
      return true;
    }
    if (entity.parent_id && entities[entity.parent_id]) {
      return hasMMSAdminEntityTypeMatch(entities, entity.parent_id, admin_entities, entity_type);
    }
  }
  return false;
}

export function hasParentMMSAdminEntityTypeMatch(entities, entity_id, admin_entities, entity_type) {
  if (!admin_entities || !admin_entities.length) {
    // eslint-disable-next-line no-param-reassign
    admin_entities = getAdminEntities();
  }
  if (admin_entities && admin_entities.length) {
    return hasMMSAdminEntityTypeMatch(entities, entity_id, admin_entities, entity_type);
  }
  return false;
}

export function hasAdminMMSEntityNonRecursive(entities, admin_entities) {
  if (!admin_entities || !admin_entities.length) {
    // eslint-disable-next-line no-param-reassign
    admin_entities = getAdminEntities();
  }
  let match = false;
  if (admin_entities && admin_entities.length) {
    Object.values(entities).forEach((e) => {
      if (match) {
        return;
      }
      if (e.entity_type === ACCOUNT && e.data.has_mms && admin_entities.includes(e.id)) {
        match = true;
      }
    });
  }

  return match;
}

export function hasEntityByRegistrationToken(entities, registration_code) {
  const entity = findEntityByToken(entities, registration_code);
  if (entity) {
    return isMemberEntity(entity.id) || isAdminEntity(entity.id);
  }

  return false;
}

function findEntityByToken(entities, registration_code) {
  return Object.values(entities).find(
    (e) => e.data && e.data.registration_code === registration_code
  );
}

export function isAccount(entity) {
  return entity && entity.entity_type === ACCOUNT;
}

export function isDistributor(entity) {
  return entity && entity.entity_type === DISTRIBUTOR;
}

export function isAsset(entity) {
  return entity && entity.entity_type === ASSET;
}

export function isUndefined(entity) {
  return entity && entity.entity_type === UNDEFINED;
}

export function isDevice(entity) {
  return entity && entity.entity_type === 'DEVICE';
}

export function getEntityTypeName(entity, formatMessage) {
  if (isAccount(entity)) {
    return formatMessage(messages.ACCOUNT);
  }
  if (isDistributor(entity)) {
    return formatMessage(messages.DISTRIBUTOR);
  }
  if (isAsset(entity)) {
    return formatMessage(messages.ASSET);
  }
  if (isUndefined(entity)) {
    return formatMessage(messages.UNDEFINED);
  }
  if (isDevice(entity)) {
    return formatMessage(messages.DEVICE);
  }
  return '';
}

export function isDeviceOnline(device, overwrite_offline_time = null) {
  if (device && device.last_seen) {
    const is_lupus = isLupusDevice(device);
    const is_efento = isEfentoDevice(device);

    const last_seen_sec = (new Date().getTime() - new Date(device.last_seen).getTime()) / 1000;
    const config = getCurrentConfigState(device)[1];
    const ble_enabled = config && config.ble_turnoff_time === 4294967295;
    const ble_warning = ble_enabled && device.current_event && device.current_event.ble;

    // check if device is efento and if efento_auto_tx_interval is true and use fix 4h30m as offline time
    const efento_auto_tx_interval = device.config && device.config.efento_auto_tx_interval;

    if (is_efento && efento_auto_tx_interval && last_seen_sec < 16200) {
      return [true, null];
    }

    if (
      config &&
      config.transmission_interval &&
      config.transmission_interval > 0 &&
      !ble_warning
    ) {
      if (last_seen_sec < config.transmission_interval * 1.1) {
        return [true, null];
      }
    }
    if (ble_warning && config && config.measurement_base && config.measurement_factor) {
      const measurement_interval = config.measurement_base * config.measurement_factor;
      if (last_seen_sec < measurement_interval * 1.1) {
        return [true, last_seen_sec];
      }
    }

    let offline_time = 15; // minuten
    if (is_lupus) {
      // 10 days in minutes (maximum time for smoke detector to send heartbeat)
      offline_time = 10 * 24 * 60;
    }
    if (
      overwrite_offline_time != null &&
      overwrite_offline_time !== '' &&
      !Number.isNaN(Number(overwrite_offline_time))
    ) {
      offline_time = Number(overwrite_offline_time);
    } else if (
      !is_lupus &&
      device.config &&
      device.config.offline_time !== '' &&
      !Number.isNaN(Number(device.config.offline_time))
    ) {
      offline_time = Number(device.config.offline_time);
    }
    if (last_seen_sec / 60 < offline_time) {
      return [true, null];
    }
    return [false, last_seen_sec];
  }

  return [false, null];
}

export function getCurrentBatteryState(device) {
  if (device && device.current_state && device.current_state.battery) {
    if (device.current_state.battery.length > 0) {
      const battery_state = device.current_state.battery[0];
      if (battery_state && battery_state.d) {
        return [battery_state.t, battery_state.d, battery_state.d === 'low'];
      }
    }
  } else if (device && device.current_state && device.current_state.lupus) {
    // loop over all battery states and return the most recent one
    const [current_state_timestamp, lupus_device_info] = getCurrentLupusDeviceInfoState(device);
    if (lupus_device_info) {
      if (lupus_device_info.batteryState === 'ERROR') {
        return [current_state_timestamp, lupus_device_info, true];
      }
      if (lupus_device_info.batteryLevel < 2.7 && lupus_device_info.batteryLevel > 1) {
        return [current_state_timestamp, lupus_device_info, true];
      }
      return [current_state_timestamp, lupus_device_info, false];
    }
  }

  return [null, null, false];
}

export function getCurrentDeviceState(device) {
  if (device && device.current_state && device.current_state.device_state) {
    if (device.current_state.device_state.length > 0) {
      return [device.current_state.device_state[0].t, device.current_state.device_state[0].d];
    }
  }

  return [null, null];
}

export function getCurrentDeviceInfoState(device) {
  if (device && device.current_state && device.current_state.device_info) {
    if (device.current_state.device_info.length > 0) {
      return [device.current_state.device_info[0].t, device.current_state.device_info[0].d];
    }
  }

  return [null, null];
}

export function getCurrentLupusDeviceInfoState(device) {
  if (device && device.current_state && device.current_state.lupus) {
    if (device.current_state.lupus.length > 0) {
      return [device.current_state.lupus[0].t, device.current_state.lupus[0].d];
    }
  }

  return [null, null];
}

export function getCurrentRelayState(device) {
  // if (device && device.current_state && device.current_state.relay_state) {
  //   // TODO: legacy, remove when all devices are migrated
  //   // loop over all relay states and return the most recent one
  //   let current_state_timestamp = null;
  //   Object.keys(device.current_state.relay_state).forEach((state_timestamp) => {
  //     if (!current_state_timestamp || current_state_timestamp < state_timestamp) {
  //       current_state_timestamp = state_timestamp;
  //     }
  //   });
  //   if (current_state_timestamp) {
  //     return [current_state_timestamp, device.current_state.relay_state[current_state_timestamp]];
  //   }
  // }
  if (device && device.current_state && device.current_state.alert_device) {
    if (device.current_state.alert_device.length > 0) {
      return [device.current_state.alert_device[0].t, device.current_state.alert_device[0].d];
    }
  }

  return [null, null];
}

export function getCurrentConfigState(device) {
  if (
    device &&
    device.current_state &&
    device.current_state.config &&
    device.current_state.config.length > 0
  ) {
    return [device.current_state.config[0].t, device.current_state.config[0].d];
  }

  return [null, null];
}

export function getCurrentBLEState(device) {
  if (
    device &&
    device.current_state &&
    device.current_state.ble_state &&
    device.current_state.ble_state.length > 0
  ) {
    return [device.current_state.ble_state[0].t, device.current_state.ble_state[0].d];
  }

  return [null, null];
}

// "new_config": [
//     {
//       "d": {
//         "ack_interval": 4294967295,
//         "transmission_interval": 3600
//       },
//       "s": 2,
//       "t": 1748993135
//     },
//     {
//       "d": {
//         "ack_interval": 4294967295,
//         "transmission_interval": 3600
//       },
//       "s": 3,
//       "t": 1748664747
//     }
//   ],

export function getNewConfigState(device) {
  if (
    device &&
    device.current_state &&
    device.current_state.new_config &&
    device.current_state.new_config.length > 0 &&
    device.current_state.new_config[0].s === 1 // 1 = new, 2 = unconfirmed, 3 = confirmed
  ) {
    return [device.current_state.new_config[0].t, device.current_state.new_config[0].d];
  }

  return [null, null];
}

export function getUnconfirmedConfigState(device) {
  if (
    device &&
    device.current_state &&
    device.current_state.new_config &&
    device.current_state.new_config.length > 0 &&
    device.current_state.new_config[0].s === 2 // 1 = new, 2 = unconfirmed, 3 = confirmed
  ) {
    return [device.current_state.new_config[0].t, device.current_state.new_config[0].d];
  }

  return [null, null];
}

export function getCurrentLocationState(device) {
  if (device && device.current_state && device.current_state.location) {
    if (device.current_state.location.length > 0) {
      return [device.current_state.location[0].t, device.current_state.location[0].d];
    }
  }

  return [null, null];
}

export function hasCurrentInvalidMeasurementState(device) {
  if (
    device &&
    device.current_event &&
    device.current_event.iv &&
    !isEmpty(device.current_event.iv)
  ) {
    return true;
  }

  return false;
}

export function getCurrentIMEIState(device) {
  if (device && device.current_state && device.current_state.imei) {
    if (device.current_state.imei.length > 0) {
      return [device.current_state.imei[0].t, device.current_state.imei[0].d];
    }
  }

  return null;
}

export function getCurrentLANIPState(device) {
  if (device && device.current_state && device.current_state.device_state) {
    if (device.current_state.device_state.length > 0) {
      const device_state = device.current_state.device_state[0];
      if (device_state && device_state.d && device_state.d.lan_ip) {
        return [device_state.t, device_state.d.lan_ip];
      }
    }
  }

  return [null, null];
}

export function getCurrentWANIPState(device) {
  if (device && device.current_state && device.current_state.device_state) {
    if (device.current_state.device_state.length > 0) {
      const device_state = device.current_state.device_state[0];
      if (device_state && device_state.d && device_state.d.wan_ip) {
        return [device_state.t, device_state.d.wan_ip];
      }
    }
  }

  return [null, null];
}

export function getCurrentMACState(device) {
  if (device && device.current_state && device.current_state.device_state) {
    if (device.current_state.device_state.length > 0) {
      const device_state = device.current_state.device_state[0];
      if (device_state && device_state.d && device_state.d.mac) {
        return [device_state.t, device_state.d.mac];
      }
    }
  }

  return [null, null];
}

export function configHasErrors(config) {
  if (config && config.errors) {
    return isEmpty(config.errors) === false;
  }

  return false;
}

export function getCurrentFWVersion(device_info) {
  if (device_info && device_info.fw_version) {
    return device_info.fw_version;
  }
  if (device_info && device_info.firmwareVersion) {
    return device_info.firmwareVersion;
  }

  return null;
}

export function getCurrentRSSIState(device) {
  if (device && device.current_state && device.current_state.device_state) {
    if (device.current_state.device_state.length > 0) {
      const device_state = device.current_state.device_state[0];
      if (device_state && device_state.d && device_state.d.rssi) {
        return [device_state.t, device_state.d.rssi];
      }
    }
  }

  return [null, null];
}
